import { Check } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '../../../../lib/utils'
import { CoiMode, type InsurancePolicy } from '../../../../models/insurancePolicy'

/**
 * Which policies the new holder's certificate states.
 *
 * Automatic policies combine freely — one certificate can state auto liability
 * and cargo together. A manual policy cannot: we are not allowed to issue its
 * certificate, so it is always selected on its own and picking it clears
 * everything else (and vice versa).
 *
 * `certifiedIds` are the policies a certificate has actually been generated for.
 * An automatic policy outside that set has nothing to send — its policy document
 * is missing, or generation has not finished — so it cannot be picked yet. A
 * manual policy never has one: each holder's copy is uploaded by hand.
 */
export function PolicySelectionPicker({
  policies,
  selectedIds,
  certifiedIds,
  onChange,
}: {
  policies: InsurancePolicy[]
  selectedIds: string[]
  certifiedIds: Set<string>
  onChange: (ids: string[]) => void
}) {
  const selected = policies.filter((policy) => selectedIds.includes(policy.id))
  const manualSelected = selected.some((policy) => policy.coiMode === CoiMode.MANUAL)

  function isPending(policy: InsurancePolicy) {
    return policy.coiMode !== CoiMode.MANUAL && !certifiedIds.has(policy.id)
  }

  function isDisabled(policy: InsurancePolicy) {
    if (isPending(policy)) return true
    if (!selected.length) return false
    const thisIsManual = policy.coiMode === CoiMode.MANUAL
    const alreadySelected = selectedIds.includes(policy.id)
    if (alreadySelected) return false
    // Manual never shares a certificate with anything else.
    return manualSelected || thisIsManual
  }

  function toggle(policy: InsurancePolicy) {
    if (selectedIds.includes(policy.id)) {
      onChange(selectedIds.filter((id) => id !== policy.id))
      return
    }
    if (policy.coiMode === CoiMode.MANUAL) {
      onChange([policy.id])
      return
    }
    onChange([...selectedIds, policy.id])
  }

  const label = selected.length
    ? selected.map((policy) => policy.vendorName || policy.label).join(' + ')
    : 'Select policies…'

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-between font-normal">
          <span className={cn('truncate', !selected.length && 'text-muted-foreground')}>{label}</span>
          {manualSelected && (
            <Badge variant="outline" className="ml-2 shrink-0 text-xs">
              Requested from Cai
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-1" align="start">
        {policies.map((policy) => {
          const checked = selectedIds.includes(policy.id)
          const disabled = isDisabled(policy)
          return (
            <button
              key={policy.id}
              type="button"
              disabled={disabled}
              onClick={() => toggle(policy)}
              className={cn(
                'hover:bg-accent flex w-full items-center gap-3 rounded-sm px-2 py-2 text-left text-sm',
                disabled && 'cursor-not-allowed opacity-50 hover:bg-transparent'
              )}
            >
              <span
                className={cn(
                  'flex size-4 shrink-0 items-center justify-center rounded-sm border',
                  checked && 'bg-primary border-primary text-primary-foreground'
                )}
              >
                {checked && <Check className="size-3" />}
              </span>
              <span className="flex-1 truncate">{policy.vendorName || policy.label}</span>
              {policy.coiMode === CoiMode.MANUAL ? (
                <Badge variant="outline" className="text-xs">
                  Manual
                </Badge>
              ) : (
                isPending(policy) && (
                  <Badge variant="outline" className="text-xs">
                    Certificate pending
                  </Badge>
                )
              )}
            </button>
          )
        })}
      </PopoverContent>
    </Popover>
  )
}
